'use client';

import { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import cx from 'clsx';
import gsap from 'gsap';

import styles from './BlurOverlay.module.scss';

const EXIT_DURATION = 0.15;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface BlurOverlayProps {
  open: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
}

const BlurOverlay = ({ open, onClose, onCloseComplete }: BlurOverlayProps) => {
  const [visible, setVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      setVisible(false);
      wasOpenRef.current = true;

      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });

      return () => cancelAnimationFrame(frame);
    }

    if (!wasOpenRef.current || !overlayRef.current) return undefined;

    wasOpenRef.current = false;

    if (prefersReducedMotion()) {
      onCloseComplete?.();

      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: EXIT_DURATION,
        ease: 'power2.in',
        onComplete: onCloseComplete,
      });
    });

    return () => ctx.revert();
  }, [open, onCloseComplete]);

  return <Box ref={overlayRef} className={cx(styles.overlay, { [styles.visible]: visible })} onClick={onClose} />;
};

export default BlurOverlay;
